import React from "react";

import { css } from "@emotion/css";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { NurseInclusionPatient } from "@/models/NurseInclusionPatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { NurseInclusionSource } from "@/uiKit/molecules/InclusionCenter/NurseInclusion/NurseInclusionSource.tsx";
import { PatientSuggestionCard } from "@/uiKit/molecules/InclusionCenter/PatientSuggestionCard.tsx";
import { NurseInclusionPatientCreation } from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusionPatientCreation.tsx";

export type NurseInclusionSuggestionProps = {
  suggestion: NurseInclusionPatient;
  onAction?: () => void;
  className?: string;
};

export const NurseInclusionSuggestion: React.FC<NurseInclusionSuggestionProps> = ({
  suggestion,
  onAction,
  className,
}) => {
  const navigate = useNavigate();
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const [isPatientCreationOpened, setPatientCreationOpened] = React.useState(false);
  const rejectInclusion = Queries.practitioner.useRejectNurseInclusionPatient();
  const nurse = suggestion.preIncludedBy;

  return (
    <>
      <PatientSuggestionCard
        patientId={suggestion.id}
        patientFirstName={suggestion.firstName}
        patientLastName={suggestion.lastName}
        subHeaderTag={<Tag color="purple" label={t("nurseInclusion.inclusionProposal")} />}
        onValidate={() => setPatientCreationOpened(true)}
        onDecline={() => rejectInclusion.mutate(suggestion.id, {})}
        source={<NurseInclusionSource id={`inclusion-nurse-suggestion-source-${suggestion.id}`} nurse={nurse} />}
        className={className}
      >
        <Tooltip
          id={`inclusion-nurse-suggestion-reason-${suggestion.id}`}
          placement="bottom-start"
          hover={{ handleClose: null }}
          content={<Typography className={styles.reasonTooltip}>{suggestion.inclusionReason}</Typography>}
        >
          <Typography variant="bodySmall" className={styles.reason}>
            {suggestion.inclusionReason}
          </Typography>
        </Tooltip>
      </PatientSuggestionCard>
      <NurseInclusionPatientCreation
        id="patient-creation-from-nurse-inclusion"
        open={isPatientCreationOpened}
        defaultValues={{
          givenName: suggestion.firstName,
          familyName: suggestion.lastName,
          email: suggestion.email,
          phone: suggestion.phoneNumber,
          helperPhone: suggestion.helperPhone,
          birthDate: suggestion.birthDate,
          diabetesType: suggestion.conditionType,
          insulinScheme: suggestion.conditionMedicationSchema,
          pump: suggestion.pumpModel,
          termDate: suggestion.termDate,
        }}
        nurseInclusion={suggestion}
        justification={suggestion.inclusionReason}
        nurse={{
          firstName: nurse.firstName,
          lastName: nurse.lastName,
        }}
        onClose={() => setPatientCreationOpened(false)}
        onReject={() => {
          rejectInclusion.mutate(suggestion.id, {
            onSuccess: () => {
              setPatientCreationOpened(false);
              onAction?.();
            },
          });
        }}
        onSubmit={(patientId: string) => {
          TextToast.success(t("diabetesForms.patientCreated"), t("diabetesForms.clickToView"), {
            onClick: () =>
              navigate({
                to: `/patients/${patientId}`,
              }),
          });
          onAction?.();
          setPatientCreationOpened(false);
        }}
      />
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    borderRadius: theme.spacing(4),
  }),
  content: css({
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(5, 12),
    gap: theme.spacing(8),
  }),
  header: css({
    padding: theme.spacing(5, 12, 1, 12),
  }),
  stats: css({
    display: "flex",
    gap: theme.spacing(8),
    width: "fit-content",
  }),
  statKey: css({
    fontWeight: "bold",
  }),
  tag: css({
    backgroundColor: theme.palette.normalGlycemia.contrastText,
    color: theme.palette.normalGlycemia.dark,
  }),
  footer: css({
    display: "flex",
    flexDirection: "row",
    gap: 0,
    padding: theme.spacing(5, 12),
    justifyContent: "flex-start",
  }),
  name: css({
    textWrap: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: "bold",
  }),
  reason: css({
    fontWeight: "normal",
    lineHeight: FontSizes.body,
    minWidth: "100%",
    width: 0,
    whiteSpace: "pre-wrap",
    display: "-webkit-box",
    "-webkit-line-clamp": "3",
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  }),
  reasonTooltip: css({
    whiteSpace: "pre-wrap",
    minWidth: "100%",
  }),
  source: css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: theme.spacing(2),
    color: theme.palette.common.black,
  }),
  sourceName: css({
    color: "inherit",
    position: "absolute",
    textWrap: "nowrap",
    bottom: theme.spacing(-6),
    fontWeight: "normal",
  }),
});
